/* eslint-disable import/max-dependencies */
import type { FC } from "react";
import React from "react";
import { Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@fas/cpa-state-manager/hooks";
import {
  useLogin,
  useRestorePassword,
  useAutologin,
  useChangePassword,
} from "@fas/cpa-state-manager/redux/hooks";
import { withHookPropsState } from "@fas/cpa-cabinet-ui";
import NotificationsComponent from "@fas/cpa-cabinet-ui/lib/Notifications";
import withNotifications from "@fas/cpa-state-manager/services/notifications";
import environment from "environment";
import { Provider } from "react-redux";
import { Paper } from "@mui/material";
import { withStyles } from "@mui/styles";
import { useCookies } from "react-cookie";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import LoginForm from "../../components/Login";
import RestorePasswordForm from "../../components/RestorePassword";
import AutologinForm from "../../components/Autologin";
import ChangePasswordForm from "../../components/ChangePassword";
import configureStore from "./store";
import { openZoneBackgroundStyles } from "../../styles";
import { NO_PAYMENT_DETAILS_MODAL } from "../../constants";

const Notifications = withNotifications(NotificationsComponent);
const Background = withStyles(openZoneBackgroundStyles)(Paper);
const Login = withHookPropsState(
  LoginForm,
  (props: FirstArgument<typeof useLogin>) => {
    const [cookie, , removeCookie] = useCookies([NO_PAYMENT_DETAILS_MODAL]);

    if (cookie[NO_PAYMENT_DETAILS_MODAL]) {
      removeCookie(NO_PAYMENT_DETAILS_MODAL, {
        path: "/",
      });
    }

    return { ...useLogin(props) };
  }
);
const Autologin = withHookPropsState(
  AutologinForm,
  (props: FirstArgument<typeof useAutologin>) => {
    const [cookie, , removeCookie]: [any, any, any] = useCookies([
      NO_PAYMENT_DETAILS_MODAL,
    ]);

    if (cookie[NO_PAYMENT_DETAILS_MODAL]) {
      removeCookie(NO_PAYMENT_DETAILS_MODAL, {
        path: "/",
      });
    }

    return { ...useAutologin(props) };
  }
);
const ChangePassword = withHookPropsState(
  ChangePasswordForm,
  useChangePassword
);
const RestorePassword = withHookPropsState(
  RestorePasswordForm,
  useRestorePassword
);

const routerComponents = {
  Login: () => (
    <GoogleReCaptchaProvider reCaptchaKey={environment.reCaptchaKey}>
      <Login
        hookProps={{ enableGoogleRecaptcha: !!environment.reCaptchaKey }}
      />
    </GoogleReCaptchaProvider>
  ),
  Autologin: () => <Autologin hookProps={{ enableGoogleRecaptcha: false }} />,
  ChangePassword: () => (
    <ChangePassword hookProps={{ enableGoogleRecaptcha: false }} />
  ),
  RestorePassword: () => (
    <RestorePassword hookProps={{ enableGoogleRecaptcha: false }} />
  ),
};
type AuthProps = {
  redirectUrl?: string;
  Component: FC;
};

const Auth = ({ redirectUrl = "/", Component }: AuthProps) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to={redirectUrl} />;
  }

  return <Component />;
};

export const AuthLogin = () => (
  <Auth
    Component={routerComponents.Login}
    redirectUrl={environment.links.home}
  />
);
export const AuthAutologin = () => (
  <Auth
    Component={routerComponents.Autologin}
    redirectUrl={environment.links.home}
  />
);
export const AuthChangePassword = () => (
  <Auth
    Component={routerComponents.ChangePassword}
    redirectUrl={environment.links.home}
  />
);
export const AuthRestorePassword = () => (
  <Auth
    Component={routerComponents.RestorePassword}
    redirectUrl={environment.links.home}
  />
);

const store = configureStore();

const AuthLayout = () => (
  <Provider store={store}>
    <Background>
      <Notifications />
      <Outlet />
    </Background>
  </Provider>
);

export default AuthLayout;
